import { AuthService } from './AuthService';
import {
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import Pool from '../../Config/UserPool';
import httpService from '../httpService';
import { ApiEndpoints } from '../../constants/appConstants';
import { SessionStorageService } from '../SessionStorageService';
import logger from '../../utils/logger';
import { notifyUserLogout } from './logoutUser';

export class CognitoAuthService implements AuthService {
  private async getUserInfo(): Promise<any> {
    return new Promise((resolve, reject) => {
      const user = Pool.getCurrentUser();

      if (!user) {
        reject(new Error('No user is currently signed in.'));
        return;
      }

      user.getSession((err: any, session: any) => {
        if (err) {
          logger.error(
            'CognitoAuthService.getUserInfo failed to get user session',
            err
          );
          reject(err);
          return;
        }

        if (!session?.isValid()) {
          reject(new Error('Session is invalid'));
          return;
        }

        user.getUserAttributes(async (attributeError: any, attributes: any) => {
          if (attributeError) {
            logger.error(
              'CognitoAuthService.getUserInfo failed to get user attributes',
              attributeError
            );
            reject(attributeError);
            return;
          }

          try {
            const identityToken =
              user.getSignInUserSession()?.getIdToken();

            const authToken =
              identityToken?.getJwtToken();

            SessionStorageService.setAuthToken(
              authToken ?? ''
            );

            let groups =
              identityToken?.payload['cognito:groups'];

            await this.getAuthorizationToken();

            const authorizationToken =
              SessionStorageService.getAuthorizationToken() ?? '';

            const roles =
              this.getRolesFromAuthorizationToken(
                authorizationToken
              );

            if (roles.length > 0) {
              groups = roles;
            }

            const userAttributes = attributes.reduce(
              (acc: any, attribute: any) => {
                acc[attribute.getName()] =
                  attribute.getValue();
                return acc;
              },
              {}
            );

            resolve({
              ...userAttributes,
              groups,
            });
          } catch (error) {
            logger.error(
              'CognitoAuthService.getUserInfo failed while processing user information',
              error
            );
            reject(error);
          }
        });
      });
    });
  }

  login(
    username?: string,
    password?: string
  ): Promise<void> {
    if (!username || !password) {
      throw new Error(
        'Username and password are required for Cognito login.'
      );
    }

    return new Promise<void>((resolve, reject) => {
      const authenticationDetails =
        new AuthenticationDetails({
          Username: username,
          Password: password,
        });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool,
      });

      cognitoUser.authenticateUser(
        authenticationDetails,
        {
          onSuccess: async () => {
            try {
              await this.getUserInfo();

              logger.info(
                'Cognito authentication successful'
              );

              resolve();
            } catch (error) {
              logger.error(
                'CognitoAuthService.login failed while loading user information',
                error
              );
              reject(error);
            }
          },
          onFailure: (error) => {
            logger.error(
              'CognitoAuthService.login authentication failed',
              error
            );
            reject(error);
          },
        }
      );
    });
  }

  async logout(
    _forceLogout: boolean = false
  ): Promise<void> {
    try {
      await notifyUserLogout();
    } catch (error) {
      logger.error(
        'CognitoAuthService.logout failed to notify application',
        error
      );
    }

    try {
      const user = Pool.getCurrentUser();

      SessionStorageService.clearAllTokens();

      if (user) {
        user.signOut();

        logger.info(
          'Cognito user logged out successfully'
        );
      } else {
        logger.info(
          'No active Cognito user found during logout'
        );
      }
    } catch (error) {
      logger.error(
        'CognitoAuthService.logout failed',
        error
      );

      SessionStorageService.clearAllTokens();

      throw error;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const user = Pool.getCurrentUser();
      return !!user;
    } catch (error) {
      logger.error(
        'CognitoAuthService.isAuthenticated failed',
        error
      );
      return false;
    }
  }

  async hasActiveSession(): Promise<boolean> {
    try {
      const user = Pool.getCurrentUser();
      return !!user;
    } catch (error) {
      logger.error(
        'CognitoAuthService.hasActiveSession failed',
        error
      );
      return false;
    }
  }

  getUser(): Promise<any> {
    return new Promise((resolve, reject) => {
      const user = Pool.getCurrentUser();

      if (!user) {
        reject(new Error('No user is currently signed in.'));
        return;
      }

      user.getSession((err: any, session: any) => {
        if (err) {
          logger.error(
            'CognitoAuthService.getUser failed to get user session',
            err
          );
          reject(err);
          return;
        }

        if (!session?.isValid()) {
          reject(new Error('Session is invalid'));
          return;
        }

        user.getUserAttributes(
          async (attributeError: any, attributes: any) => {
            if (attributeError) {
              logger.error(
                'CognitoAuthService.getUser failed to get user attributes',
                attributeError
              );
              reject(attributeError);
              return;
            }

            try {
              const identityToken =
                user.getSignInUserSession()?.getIdToken();

              const authToken =
                identityToken?.getJwtToken();

              SessionStorageService.setAuthToken(
                authToken ?? ''
              );

              let groups =
                identityToken?.payload['cognito:groups'];

              await this.getAuthorizationToken();

              const authorizationToken =
                SessionStorageService.getAuthorizationToken() ??
                '';

              const roles =
                this.getRolesFromAuthorizationToken(
                  authorizationToken
                );

              if (roles.length > 0) {
                groups = roles;
              }

              const userAttributes = attributes.reduce(
                (acc: any, attribute: any) => {
                  acc[attribute.getName()] =
                    attribute.getValue();
                  return acc;
                },
                {}
              );

              if (!userAttributes.name) {
                userAttributes.name =
                  `${userAttributes.given_name || ''} ${
                    userAttributes.family_name || ''
                  }`.trim();
              }

              resolve({
                ...userAttributes,
                groups,
              });
            } catch (error) {
              logger.error(
                'CognitoAuthService.getUser failed while processing user information',
                error
              );
              reject(error);
            }
          }
        );
      });
    });
  }

  async loginWithoutCredentials(): Promise<boolean> {
    return false;
  }

  async getAuthorizationToken(): Promise<void> {
    try {
      const response = await httpService.get<any>(
        ApiEndpoints.getAuthorizationToken
      );

      const authorizationToken =
        response.data?.token ??
        response.data?.Token;

      if (!authorizationToken) {
        throw new Error(
          'Authorization token was not returned'
        );
      }

      SessionStorageService.setAuthorizationToken(
        authorizationToken
      );
    } catch (error) {
      logger.error(
        'CognitoAuthService.getAuthorizationToken failed',
        error
      );
      throw error;
    }
  }

  getRolesFromAuthorizationToken(
    token: string
  ): string[] {
    if (!token) {
      return [];
    }

    try {
      const tokenParts = token.split('.');

      if (tokenParts.length !== 3) {
        logger.warn(
          'Authorization token has invalid JWT format'
        );
        return [];
      }

      const base64Url = tokenParts[1];
      const base64 = base64Url
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      const paddedBase64 =
        base64 +
        '='.repeat((4 - (base64.length % 4)) % 4);

      const payload = JSON.parse(
        atob(paddedBase64)
      );

      let roles = payload?.role || [];

      if (!Array.isArray(roles)) {
        roles = [roles];
      }

      return roles.filter(
        (role: unknown): role is string =>
          typeof role === 'string' &&
          role.trim().length > 0
      );
    } catch (error) {
      logger.error(
        'CognitoAuthService.getRolesFromAuthorizationToken failed to decode roles',
        error
      );
      return [];
    }
  }
}